import React from 'react';
import { VStack, Text } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import EventTaskSidebar from './task-types/event/EventTaskSidebar';
import EssayTaskSidebar from './task-types/essay/EssayTaskSidebar';
import { taskRequiresEvaluation } from '../../../common/utils/taskEvaluationUtils';
import { useTenantEventKinds } from '../../../common/context/TenantSettingsStore';
import { isEventDeliveredTask } from '../../../common/utils/tasks/isEventDeliveredTask';
import { isOralExamTask } from '../../../common/utils/tasks/isOralExamTask';
import { wordCountBounds } from '../../../common/utils/wordCount';

const TaskRequirement = ({ title, value }) => (
  <VStack>
    <Text mb="$1" color="$textHint" fontSize={8} textTransform="uppercase">
      {title}
    </Text>
    <Text size="xs" lineHeight="$xs">
      {value}
    </Text>
  </VStack>
);

/**
 * TaskSidebarSelector
 * ------------------
 * Selects sidebar implementation based on event-delivery semantics.
 * Falls back to EssayTaskSidebar for non-event tasks.
 */
const TaskSidebarSelector = ({
  task,
  tasks,
  children,
  ...props
}) => {
  const { t } = useTranslation();
  const { eventKinds } = useTenantEventKinds();

  const hasOralExamTask = React.useMemo(() => {
    if (!tasks) return false;
    return tasks.some((t) => t.type === 'oral_exam');
  }, [tasks]);

  if (!task) return null;

  const formalReqs = task?.evaluation?.formalCriteria;
  const wordCountRange = wordCountBounds(formalReqs);
  const wordcountRequirement = wordCountRange
    ? t('student.module.task-sidebar.wordCountTargetValue', {
      count: wordCountRange.target,
      tolerance: ((formalReqs?.wordcountTolerance ?? 0) * 100).toFixed(0),
    })
    : null;
  const gradingType = task?.evaluation?.mode === 'auto' ? 'plt' : task?.gradingSystem;
  const evaluationRequired = taskRequiresEvaluation(task, eventKinds);
  const oralExam = isOralExamTask(task);
  const oralExamDone = oralExam && task.submission?.state === 'marked_as_done';

  const evaluationValue = !evaluationRequired
    ? 'Not subject to evaluation'
    : oralExamDone
      ? t('student.module.task-sidebar.oralExamCompletedMessage')
      : task.type === 'activity'
        ? t('student.module.task-sidebar.activityMessage')
        : isEventDeliveredTask(task)
          ? t('student.module.task-sidebar.eventMessage')
          : hasOralExamTask
            ? t('student.module.task-sidebar.oral-exam-module-message')
            : t(`student.module.task-sidebar.grading-system-message.${gradingType}`);

  const details = (
    <VStack space="lg">
      <TaskRequirement title={t('student.module.task-sidebar.evaluation')} value={evaluationValue} />

      {task.type === 'essay' && (
        <TaskRequirement
          title={t('student.module.task-sidebar.citations-requirement')}
          value={
            task.evaluation.formalCriteria.minInTextCitations
              ? t('student.module.task-sidebar.citations-required')
              : t('student.module.task-sidebar.citations-not-required')
          }
        />
      )}

      {task.type === 'essay' && wordcountRequirement && (
        <TaskRequirement title={t('student.module.task-sidebar.target-wordcount')} value={wordcountRequirement} />
      )}

      {task.type === 'multiple_choice' && !!task.mcqConfig?.timeLimitMinutes && (
        <TaskRequirement
          title={t('student.module.task-sidebar.timeLimit')}
          value={t('student.module.task-sidebar.timeLimitValue', { minutes: task.mcqConfig.timeLimitMinutes })}
        />
      )}

      <TaskRequirement
        title={t('student.module.task-sidebar.completionPolicy')}
        value={oralExam
          ? t('student.module.task-sidebar.oralExamCompletionPolicy')
          : t(`student.module.task-sidebar.completionPolicies.${task.evaluation.completionPolicyEffective}`)}
      />
    </VStack>
  );

  if (isEventDeliveredTask(task)) {
    return (
      <EventTaskSidebar task={task} {...props}>
        {details}
        {children}
      </EventTaskSidebar>
    );
  }

  return (
    <EssayTaskSidebar task={task} {...props}>
      {details}
      {children}
    </EssayTaskSidebar>
  );
};

export default TaskSidebarSelector;
